import { useEffect } from "react";
import { usePDFSlick } from "@pdfslick/react";
import "@pdfslick/react/dist/pdf_viewer.css";
import { GlobalWorkerOptions } from "pdfjs-dist";
import {
  X,
  Download,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
} from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { capture } from "@/lib/posthog";

// PDFSlick's core points the worker at a bare specifier that Bun can't resolve;
// override it with the worker copied to /pdf.worker.min.mjs (build.ts + index.ts).
GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

/**
 * Visor de un documento. Va en un componente aparte y montado con `key={url}`
 * a propósito: `usePDFSlick` crea su store con `useMemo(..., [])`, así que ese
 * estado sobrevive al cambio de documento. Sin remontar, al abrir el segundo PDF
 * `pagesReady` ya viene en `true` del anterior y el "cargando" nunca aparece.
 */
function PdfDocument({
  url,
  title,
  initialPage,
  onClose,
}: {
  url: string;
  title?: string;
  initialPage?: number;
  onClose: () => void;
}) {
  const { viewerRef, usePDFSlickStore, PDFSlickViewer } = usePDFSlick(url, {
    scaleValue: "page-width",
    // Local pdf.js decoder/asset URLs so JBIG2/JPEG2000 images, CMaps and
    // standard fonts render (served by build.ts + src/index.ts).
    getDocumentParams: {
      wasmUrl: "/pdfjs/wasm/",
      cMapUrl: "/pdfjs/cmaps/",
      cMapPacked: true,
      standardFontDataUrl: "/pdfjs/standard_fonts/",
      iccUrl: "/pdfjs/iccs/",
    },
  });
  const pdfSlick = usePDFSlickStore((s) => s.pdfSlick);
  const pageNumber = usePDFSlickStore((s) => s.pageNumber);
  const numPages = usePDFSlickStore((s) => s.numPages);
  // `pdfSlick` solo dice que el visor se instanció; `pagesReady` es cuando la
  // página ya se pintó y el usuario puede leerla.
  const pagesReady = usePDFSlickStore((s) => s.pagesReady);

  // Abrir en la página citada. Se espera a `pagesReady` porque antes de eso
  // `gotoPage` no tiene a dónde ir, y no se remonta al cambiar solo la página
  // (la `key` es la url), así que el efecto tiene que reaccionar a ella.
  useEffect(() => {
    if (pagesReady && initialPage && initialPage > 1) {
      pdfSlick?.gotoPage(initialPage);
    }
  }, [pagesReady, initialPage, pdfSlick]);

  return (
    <>
      {/* Toolbar */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-b bg-slate-50 px-4 py-3 dark:bg-slate-900/20">
        <DialogTitle className="truncate text-sm font-bold" title={title}>
          {title || "Documento"}
        </DialogTitle>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            disabled={!pdfSlick || pageNumber <= 1}
            onClick={() => pdfSlick?.gotoPage(pageNumber - 1)}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <span className="min-w-[64px] text-center text-xs tabular-nums text-muted-foreground">
            {numPages ? `${pageNumber} / ${numPages}` : "—"}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            disabled={!pdfSlick || pageNumber >= numPages}
            onClick={() => pdfSlick?.gotoPage(pageNumber + 1)}
          >
            <ChevronRight className="size-4" />
          </Button>
          <span className="mx-1 h-5 w-px bg-border" />
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            disabled={!pdfSlick}
            onClick={() => pdfSlick?.decreaseScale()}
          >
            <ZoomOut className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            disabled={!pdfSlick}
            onClick={() => pdfSlick?.increaseScale()}
          >
            <ZoomIn className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            disabled={!pdfSlick}
            onClick={() => pdfSlick?.downloadOrSave()}
            title="Descargar"
          >
            <Download className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={onClose}
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>

      {/* Viewer */}
      <div className="relative flex-1 bg-slate-200/60 dark:bg-slate-950/40">
        {/* Sin esto el modal abre en gris y parece colgado: un anexo puede pesar
            varios MB y además pasa por el proxy, que lo trae entero antes de
            servirlo. */}
        {!pagesReady && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-slate-200/60 dark:bg-slate-950/40">
            <Spinner className="size-6 text-slate-400" />
            <span className="text-xs font-semibold text-slate-500">
              Cargando documento…
            </span>
          </div>
        )}
        <PDFSlickViewer
          viewerRef={viewerRef}
          usePDFSlickStore={usePDFSlickStore}
        />
      </div>
    </>
  );
}

export function PdfViewerModal({
  url,
  title,
  initialPage,
  open,
  onOpenChange,
}: {
  url: string | null;
  title?: string;
  /** Página en la que abrir, para las citas de los requisitos. */
  initialPage?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  // Mide si los documentos se leen: es lo que justifica el pipeline de IA.
  useEffect(() => {
    if (open && url) capture("pdf_opened");
  }, [open, url]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex h-[92vh] w-[95vw] max-w-4xl! flex-col gap-0 overflow-hidden p-0"
      >
        {url ? (
          <PdfDocument
            key={url}
            url={url}
            title={title}
            initialPage={initialPage}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          <DialogTitle className="sr-only">Documento</DialogTitle>
        )}
      </DialogContent>
    </Dialog>
  );
}
